import { PHASE45_COMMS_ENABLED, PHASE45_COPY } from './phase45Comms.js';

// Plan configuration shared by the checkout flow (CreateSubscription.js), the
// Settings > Subscription surface (SubscriptionView.js) and the onboarding
// plan-overview step.
//
// Everything spent on Scribe settles against a per-therapist credit wallet.
// 1 credit = HK$5. The plan grants a monthly credit allotment; individual
// actions cost credits. The browser NEVER chooses a Stripe price: each plan
// exposes a `tier` and the subscription backend resolves the tier to the
// current Stripe price server-side, from the verified session.

export const CREDIT_HKD = 5;

// Per-action prices, in credits. Display copy only — the server calculates and
// applies every real charge.
export const CREDIT_COSTS = {
  liveTranscriptionPerHour: 3,
  recordedTranscriptionPerHour: 1,
  report: 1,
  clientConnectionPerMonth: 10,
};

// How many credit-paid connections a plan's monthly allotment funds
// (floor(allotment / 10)). Since recharge shipped this is NO LONGER a hard cap —
// credit-paid connections are limited only by wallet balance (10 credits each),
// so this number is indicative of what the allotment alone covers, not a ceiling.
const connectionCapacity = (credits) =>
  Math.floor(credits / CREDIT_COSTS.clientConnectionPerMonth);

export const SUBSCRIPTION_PLANS = {
  FREE: {
    name: 'Free',
    tier: 'free',
    description: 'Explore TikCare with a one-time credit trial.',
    // Legacy metering fields — removed in Phase 3, when spending moves to the
    // server-authoritative wallet. The pre-Phase-3 recorded-transcription
    // limiter and the server-side activate_free_plan() legacy columns still
    // read them.
    transcription_seconds_limit: 7200, // removed in Phase 3
    audio_transcription_seconds_limit: 10800, // removed in Phase 3
    monthlyCredits: 15,
    connectionCapacity: connectionCapacity(15),
    priceCents: 0,
    price: 'HK$0',
    displayPrice: 'HK$0',
    displayPeriod: 'one-time trial',
    tagline: '1-month trial',
    features: [
      '15 trial credits, one time (worth HK$75)',
      'Enough for 1 connected client or ~5 hours of live transcription',
      'Credits expire after 1 month',
    ],
  },
  BASIC: {
    name: 'Basic',
    tier: 'basic',
    description: 'For individual practitioners.',
    transcription_seconds_limit: 36000, // removed in Phase 3
    audio_transcription_seconds_limit: 0, // removed in Phase 3
    monthlyCredits: 30,
    connectionCapacity: connectionCapacity(30),
    priceCents: 15000, // HK$150.00
    price: 'HK$150/month',
    displayPrice: 'HK$150',
    displayPeriod: 'per month',
    // D-022 annual commitment: billed MONTHLY at 10% off, 12-month minimum
    // term. Display only — the server resolves the real Stripe price.
    annual: {
      priceCents: 13500, // HK$135.00/month
      displayPrice: 'HK$135',
      displayPeriod: 'per month, billed monthly',
      note: '10% off · 12-month term',
    },
    features: [
      '30 credits every month',
      'HK$5.00 per credit',
      'About 8 sessions per month, each with 50 minutes of live transcription + 1 report. Actual capacity varies with credit usage.',
      'Credits reset monthly — no rollover',
      'Cancel anytime',
    ],
  },
  PRO: {
    name: 'Pro',
    tier: 'pro',
    description: 'For high-volume practices.',
    transcription_seconds_limit: 72000, // removed in Phase 3
    audio_transcription_seconds_limit: 0, // removed in Phase 3
    monthlyCredits: 100,
    connectionCapacity: connectionCapacity(100),
    priceCents: 45000, // HK$450.00
    price: 'HK$450/month',
    displayPrice: 'HK$450',
    displayPeriod: 'per month',
    // D-022 annual commitment: billed MONTHLY at 10% off, 12-month minimum
    // term. Display only — the server resolves the real Stripe price.
    annual: {
      priceCents: 40500, // HK$405.00/month
      displayPrice: 'HK$405',
      displayPeriod: 'per month, billed monthly',
      note: '10% off · 12-month term',
    },
    tagline: 'Most popular',
    features: [
      '100 credits every month',
      'HK$4.50 per credit — 10% less than Basic',
      'About 28 sessions per month, each with 50 minutes of live transcription + 1 report. Actual capacity varies with credit usage.',
      'Credits reset monthly — no rollover',
      'Cancel anytime',
    ],
  },
};

// Rows of the plan-comparison table. Everything is denominated in credits;
// the hour/report figures show what the whole monthly allotment buys if it is
// spent entirely on that one activity.
export const PLAN_COMPARISON_ROWS = [
  {
    label: 'Monthly credits (HK$5 each)',
    values: { FREE: '15 one-time', BASIC: '30', PRO: '100' },
  },
  {
    label: 'Live transcription — 3 credits/hour',
    values: { FREE: 'up to 5 hrs', BASIC: 'up to 10 hrs', PRO: 'up to 33 hrs' },
  },
  {
    label: 'Recorded transcription — 1 credit/hour',
    values: { FREE: 'up to 15 hrs', BASIC: 'up to 30 hrs', PRO: 'up to 100 hrs' },
  },
  {
    label: 'Reports — 1 credit each',
    values: { FREE: 'up to 15', BASIC: 'up to 30', PRO: 'up to 100' },
  },
  {
    label: 'Connected clients — 10 credits/month each',
    values: { FREE: '1', BASIC: '3', PRO: '10' },
  },
];

// MINDVAULT_PLAN_NOTE — the pre-Phase-5 "no per-client booking or access code
// required" promise is retired by Phase 5 (credit-paid connections). The
// replacement is a DRAFT (owner: product/boss will revise after hands-on
// experience), gated by PHASE45_COMMS_ENABLED: while the flag is off this
// export is byte-identical to the pre-draft note. Do not enable the flag
// without the Phase 4/5 entry gates (see src/constants/phase45Comms.js).
export const MINDVAULT_PLAN_NOTE = PHASE45_COMMS_ENABLED
  ? PHASE45_COPY.planNote.flipped
  : PHASE45_COPY.planNote.legacy;
